import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Menu, X, Moon, Sun } from "lucide-react";
import { Logo } from "./Primitives";
import { APP_URL } from "./data";
import { useThemeMode } from "./Theme";

const LINKS = [["Explainer", "#explainer"], ["Evidence", "#evidence"], ["Modules", "#modules"], ["Inside", "#inside"], ["Triage", "#triage"], ["Exports", "#exports"], ["Contact", "#contact"]];

const ThemeToggle = ({ className = "", testId, label }) => {
  const { dark, toggle } = useThemeMode();
  const Icon = dark ? Sun : Moon;
  return (
    <button onClick={toggle} data-testid={testId} aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
      className={`inline-flex items-center gap-2 rounded-md border border-[var(--line)] text-[var(--muted)] hover:text-[var(--teal)] hover:border-[var(--teal)] transition-colors ${className}`}>
      <Icon className="h-4 w-4 transition-transform duration-500 hover:rotate-45" />
      {label && <span>{dark ? "Light theme" : "Dark theme"}</span>}
    </button>
  );
};

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");
  useEffect(() => {
    const els = LINKS.map(([, h]) => document.querySelector(h)).filter(Boolean);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setActive(`#${e.target.id}`); });
    }, { rootMargin: "-45% 0px -50% 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 inset-x-0 z-50"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-4">
        <nav className="flex items-center justify-between rounded-xl border border-[var(--line)] bg-[var(--glass)] backdrop-blur-xl shadow-[0_8px_30px_-12px_rgba(17,25,23,0.15)] pl-3 pr-2 py-2">
          <a href="#top" data-testid="nav-logo-link" className="flex items-center gap-2.5">
            <Logo className="h-8 w-8" />
            <span className="font-display text-lg font-bold tracking-tight">Morphogenesis</span>
          </a>
          <div className="hidden lg:flex items-center gap-1">
            {LINKS.map(([l, h]) => (
              <a key={h} href={h} data-testid={`nav-link-${l.toLowerCase()}`} className={`relative px-4 py-2 text-sm transition-colors ${active === h ? "text-[var(--teal)]" : "text-[var(--muted)] hover:text-[var(--teal)]"}`}>{l}{active === h && <motion.span layoutId="nav-active" className="absolute left-4 right-4 -bottom-0.5 h-[2px] rounded-full bg-[var(--teal)]" />}</a>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle testId="nav-theme-toggle" className="hidden lg:inline-flex h-9 w-9 justify-center" />
            <a href={APP_URL} target="_blank" rel="noreferrer" data-testid="nav-launch-app-btn"
              className="group inline-flex items-center gap-1.5 rounded-md bg-[var(--teal)] px-4 py-2 text-sm font-semibold text-[var(--on-teal)] hover:bg-[var(--text)] transition-colors">
              Open app <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
            <button onClick={() => setOpen(!open)} data-testid="nav-mobile-menu-btn" aria-label="Menu" className="lg:hidden grid place-items-center h-9 w-9 rounded-md border border-[var(--line)]">
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </nav>
        {open && (
          <div data-testid="nav-mobile-menu" className="lg:hidden mt-2 rounded-xl border border-[var(--line)] bg-[var(--glass)] backdrop-blur-xl p-3 grid">
            {LINKS.map(([l, h]) => (
              <a key={h} href={h} onClick={() => setOpen(false)} data-testid={`nav-mobile-link-${l.toLowerCase()}`} className="px-4 py-3 rounded-lg text-base hover:bg-[var(--surface-2)]">{l}</a>
            ))}
            <ThemeToggle testId="nav-mobile-theme-toggle" label className="mt-2 px-4 py-3 text-base" />
          </div>
        )}
      </div>
    </motion.header>
  );
};
